import ButtonGroups from "components/ButtonGroups";
import InputLabel from "components/InputLabel";
import SimpleSelect from "components/SimpleSelect";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { certificationExportStore as certExportStore } from "../CertificationExportStore";
import { globalStore } from "store/GlobalStore";
import {
  QNG_SUPPORTING_DOCS,
  BUILDING_CLASSES,
  BUILDING_TYPES,
  ENERGETICS_STANDARDS,
  LEVEL_OF_REQUIREMENTS,
} from "utils";
import { buildingStore } from "store/Building/BuildingStore";
import { ExportCertificationOptions } from "api-client";
import { useComplianceInfo } from "hooks/useComplianceInfo";

const DGNB23AndQNG = () => {
  const { t } = useTranslation();
  const { additionalInfo, activeStep } = certExportStore;
  const { currentBuilding } = buildingStore;
  const { getCurrentCertification } = useComplianceInfo();
  const currentCertification = getCurrentCertification(
    certExportStore.certification_id
  )?.name;

  const savedValues: ExportCertificationOptions | undefined | null =
    currentBuilding?.certification_export_settings?.[
      currentCertification?.includes("23") ? "dgnb23" : "qng"
    ];

  React.useEffect(() => {
    if (activeStep === 4 || activeStep === 5) {
      const savedBuildingType = savedValues?.["building_type"];
      handleOnChangeBuildingType(savedBuildingType ?? undefined);

      const savedLevel = savedValues?.["level_of_requirements"];
      handleOnChangeLevelOfRequirements(savedLevel ?? undefined);

      const savedClass = BUILDING_CLASSES.find(
        (item) => item.name === savedValues?.["lca_class"]
      )?.id;
      handleOnChangeBuildingClass(savedClass ?? undefined);

      const savedStandard = savedValues?.["energetic_standard"];
      handleOnChangeEnergeticStandard(savedStandard ?? undefined);
    }
  }, [activeStep, certExportStore.certification_id]);

  const currentBuildingType = BUILDING_TYPES.find(
    (item) =>
      item.name === additionalInfo.buildingType ||
      item.name_de === additionalInfo.buildingType
  );

  const currentLevel = LEVEL_OF_REQUIREMENTS.find(
    (item) => item.name === additionalInfo.levelOfRequirements
  );

  const currentStandard = ENERGETICS_STANDARDS.find(
    (item) =>
      item.name === additionalInfo.energeticStandard ||
      item.name_de === additionalInfo.energeticStandard
  );

  const currentClass =
    BUILDING_CLASSES.find((item) => item.name === additionalInfo.buildingClass)
      ?.id ?? "";

  const handleOnChangeBuildingType = (name: string | undefined) => {
    certExportStore.setAdditionalInfoFieldAndValue("buildingType", name);
  };

  const handleOnChangeEnergeticStandard = (name: string | undefined) => {
    certExportStore.setAdditionalInfoFieldAndValue("energeticStandard", name);
  };

  const handleOnChangeLevelOfRequirements = (level: string | undefined) => {
    certExportStore.setAdditionalInfoFieldAndValue(
      "levelOfRequirements",
      level
    );
  };

  const handleOnChangeBuildingClass = (value: string | undefined) => {
    certExportStore.handleOnDropdownChange(
      "buildingClass",
      BUILDING_CLASSES,
      value
    );
  };

  return (
    <div>
      <InputLabel
        labelName={t("certExport.buildingType")}
        name={"building_type"}
      />
      <ButtonGroups
        containerClassName="cursor-pointer mb-4"
        items={BUILDING_TYPES}
        onChange={(e) =>
          handleOnChangeBuildingType(
            globalStore.currentLanguage === "en" ? e?.name : e?.name_de
          )
        }
        current={currentBuildingType}
        required
      />
      {currentBuildingType?.name?.includes("Residential building") && (
        <>
          <InputLabel
            labelName={t("certExport.energeticStandard")}
            name="energetic_standard"
          />
          <ButtonGroups
            containerClassName="cursor-pointer mb-4"
            items={ENERGETICS_STANDARDS}
            onChange={(e) =>
              handleOnChangeEnergeticStandard(
                globalStore.currentLanguage === "en" ? e?.name : e?.name_de
              )
            }
            current={currentStandard}
            required
          />
        </>
      )}
      {currentBuildingType?.name?.includes("Non-residential") && (
        <>
          <InputLabel
            labelName={t("certExport.levelOfRequirements")}
            name={"level_of_requirements"}
          />
          <ButtonGroups
            containerClassName="cursor-pointer mb-4"
            items={LEVEL_OF_REQUIREMENTS}
            onChange={(e) => handleOnChangeLevelOfRequirements(e?.name)}
            current={currentLevel}
            required
          />
          <SimpleSelect
            labelName={t("certExport.buildingClass")}
            options={BUILDING_CLASSES}
            name="building_class"
            placeholder={t("certExport.buildingClassPlaceholder")}
            onChange={(value) => handleOnChangeBuildingClass(value)}
            current={currentClass}
            helper={
              <div
                dangerouslySetInnerHTML={{
                  __html: t("buildingSettings.lcaClassDescription", {
                    link: QNG_SUPPORTING_DOCS,
                  }),
                }}
              ></div>
            }
            required
          />
        </>
      )}
    </div>
  );
};

export default observer(DGNB23AndQNG);
